import { createReducer } from '@reduxjs/toolkit';
import {
	resetSystemConnectionStore,
	createSystemConnection,
	createSystemConnectionResolved,
	createSystemConnectionRejected,
	getSystemConnectionByIdRejected,
	getSystemConnectionByIdResolved,
	getSystemConnectionsRejected,
	getSystemConnectionsResolved,
	updateSystemConnection,
	updateSystemConnectionResolved,
	updateSystemConnectionRejected,
	testSystemConnection,
	testSystemConnectionResolved,
	testSystemConnectionRejected,
	resetSystemConnectionTestStatus,
	deleteSystemConnection,
	deleteSystemConnectionResolved,
	deleteSystemConnectionRejected,
	systemConnectionTestAsync,
	systemConnectionTestAsyncRejected,
	systemConnectionTestAsyncResolved,
	systemConnectionTestAsyncPollStop,
	systemConnectionTestAsyncPollTimeout,
} from '@Actions';

import { SystemConnectionsStoreState } from '@Types';

export const initialState: SystemConnectionsStoreState = {
	initialized: false,
	status: 'idle',
	list: [],
	testSystemConnectionStatus: 'idle',
	isCurrentlyDeleting: false,
	isUpdating: false,
};

const reducer = createReducer(initialState, builder =>
	builder
		.addCase(resetSystemConnectionStore, state => {
			state.status = 'idle';
		})
		.addCase(resetSystemConnectionTestStatus, state => {
			state.testSystemConnectionStatus = 'idle';
		})
		.addCase(createSystemConnection, state => {
			state.status = 'pending';
		})
		.addCase(createSystemConnectionResolved, state => {
			state.status = 'completed';
		})
		.addCase(createSystemConnectionRejected, state => {
			state.status = 'rejected';
		})
		.addCase(updateSystemConnection, state => {
			state.status = 'pending';
			state.isUpdating = true;
		})
		.addCase(updateSystemConnectionResolved, (state, { payload }) => {
			state.isUpdating = false;
			state.status = 'completed';
			state.list = state.list.map(current =>
				current.SystemConnectionId !== payload.connectionId
					? current
					: { ...current, ...payload },
			);
		})
		.addCase(updateSystemConnectionRejected, state => {
			state.status = 'rejected';
			state.isUpdating = false;
		})
		.addCase(deleteSystemConnection, state => {
			state.status = 'pending';
			state.isCurrentlyDeleting = true;
		})
		.addCase(deleteSystemConnectionResolved, state => {
			state.status = 'completed';
			state.isCurrentlyDeleting = false;
		})
		.addCase(deleteSystemConnectionRejected, state => {
			state.status = 'rejected';
			state.isCurrentlyDeleting = false;
		})
		.addCase(getSystemConnectionByIdResolved, (state, action) => {
			const connectionIndex = state.list.findIndex(
				({ SystemConnectionId }) =>
					SystemConnectionId === action.payload.SystemConnectionId,
			);

			state.initialized = true;

			if (connectionIndex >= 0) {
				state.list[connectionIndex] = action.payload;
			} else {
				state.list.push(action.payload);
			}

			return undefined;
		})
		.addCase(getSystemConnectionByIdRejected, state => {
			state.initialized = true;
		})
		.addCase(getSystemConnectionsResolved, (state, action) => {
			state.initialized = true;
			state.list = action.payload.systemConnections;
		})
		.addCase(getSystemConnectionsRejected, state => {
			state.initialized = true;
		})
		.addCase(testSystemConnection, state => {
			state.status = 'pending';
			state.testSystemConnectionStatus = 'pending';
		})
		.addCase(systemConnectionTestAsync, state => {
			state.status = 'pending';
			state.testSystemConnectionStatus = 'pending';
		})
		.addCase(testSystemConnectionResolved, (state, action) => {
			state.status = 'idle';
			state.testSystemConnectionStatus = action.payload;
		})
		.addCase(systemConnectionTestAsyncResolved, (state, action) => {
			state.status = 'idle';
			state.testSystemConnectionStatus = action.payload;
		})
		.addCase(systemConnectionTestAsyncPollStop, state => {
			state.status = 'idle';
			state.testSystemConnectionStatus = 'idle';
		})
		.addCase(systemConnectionTestAsyncPollTimeout, state => {
			state.status = 'idle';
			state.testSystemConnectionStatus = 'timeout';
		})
		.addCase(testSystemConnectionRejected, (state, action) => {
			state.status = 'rejected';
			state.testSystemConnectionStatus = action.payload;
		})
		.addCase(systemConnectionTestAsyncRejected, (state, action) => {
			state.status = 'rejected';
			state.testSystemConnectionStatus = action.payload;
		}),
);

export default reducer;
